import React, { useState, useEffect } from 'react';
import { Typography, message, Space, Tag, Button } from 'antd';
import { HeartOutlined, StarOutlined } from '@ant-design/icons';
import MarketplaceGrid from '../../UI/MarketplaceGrid';
import MarketplaceVendorCard from '../../UI/MarketplaceVendorCard';
import api from '../../../services/api';

const { Title } = Typography;

const VendorListing = ({ 
  title = "Restaurants & Vendors",
  showFavorite = true,
  showStats = true
}) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('name');

  const itemsPerPage = 12;

  useEffect(() => {
    loadVendors();
  }, [currentPage, filters, sortBy]);

  const loadVendors = async () => {
    try {
      setLoading(true);
      
      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        sort_by: sortBy,
        ...filters
      };

      const response = await api.get('/vendors', { params });
      
      if (response.data.success) {
        setVendors(response.data.data.data || response.data.data);
        setTotalItems(response.data.data.total || response.data.data.length);
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
      message.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = (vendor) => {
    const newFavorites = new Set(favorites);
    if (favorites.has(vendor.id)) {
      newFavorites.delete(vendor.id);
      message.info(`${vendor.business_name} removed from favorites`);
    } else {
      newFavorites.add(vendor.id);
      message.success(`${vendor.business_name} added to favorites`);
    }
    setFavorites(newFavorites);
  };

  const handleViewDetails = (vendor) => {
    // Navigate to vendor detail page
    window.location.href = `/vendor/${vendor.id}`;
  };

  const handleSearch = (searchTerm) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
    setCurrentPage(1);
  };

  const handleFilter = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleSort = (sortValue) => {
    setSortBy(sortValue);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const filterOptions = [
    {
      key: 'cuisines',
      placeholder: 'Cuisine',
      options: [
        { value: 'north_indian', label: 'North Indian' },
        { value: 'south_indian', label: 'South Indian' },
        { value: 'chinese', label: 'Chinese' },
        { value: 'continental', label: 'Continental' },
        { value: 'italian', label: 'Italian' },
        { value: 'mexican', label: 'Mexican' }
      ]
    },
    {
      key: 'rating',
      placeholder: 'Rating',
      options: [
        { value: '4+', label: '4+ Stars' },
        { value: '3+', label: '3+ Stars' },
        { value: '2+', label: '2+ Stars' }
      ]
    },
    {
      key: 'delivery_time',
      placeholder: 'Delivery Time',
      options: [
        { value: '0-30', label: 'Under 30 min' },
        { value: '30-60', label: '30-60 min' },
        { value: '60+', label: 'Over 60 min' }
      ]
    },
    {
      key: 'min_order',
      placeholder: 'Min Order',
      options: [
        { value: '0-100', label: 'Under ₹100' },
        { value: '100-300', label: '₹100-₹300' },
        { value: '300+', label: 'Over ₹300' }
      ]
    }
  ];

  const sortOptions = [
    { value: 'name', label: 'Name A-Z' },
    { value: 'rating', label: 'Rating' },
    { value: 'preparation_time', label: 'Delivery Time' },
    { value: 'min_order_amount', label: 'Min Order Amount' },
    { value: 'newest', label: 'Newest' }
  ];

  const renderVendorCard = (vendor) => (
    <MarketplaceVendorCard
      vendor={vendor}
      onViewDetails={handleViewDetails}
      onToggleFavorite={handleToggleFavorite}
      isFavorite={favorites.has(vendor.id)}
      showFavorite={showFavorite}
      showStats={showStats}
      imageHeight={180}
    />
  );

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>{title}</Title>
        <Space wrap>
          <Tag color="blue">{totalItems} vendors</Tag>
          <Tag color="green">Verified restaurants</Tag>
          <Tag color="orange">Fast delivery</Tag>
        </Space>
      </div>

      {/* Vendor Grid */}
      <MarketplaceGrid
        items={vendors}
        renderItem={renderVendorCard}
        loading={loading}
        emptyMessage="No vendors found"
        showPagination={true}
        showFilters={true}
        showSearch={true}
        showSort={true}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        totalItems={totalItems}
        onSearch={handleSearch}
        onFilter={handleFilter}
        onSort={handleSort}
        onPageChange={handlePageChange}
        searchPlaceholder="Search restaurants..."
        sortOptions={sortOptions}
        filterOptions={filterOptions}
        gridProps={{
          xs: 24,
          sm: 12,
          md: 8,
          lg: 6,
          xl: 6
        }}
      />
    </div>
  );
};

export default VendorListing;
